import {
  Card,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Popconfirm,
  Row,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import ButtonOperation from 'components/ButtonOperation';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import DeleteButton from 'components/DeleteButton';
import ParListSelect from 'components/ParListSelect';
import UpdateButton from 'components/UpdateButtonAction';
import { useFormList } from 'hooks/useFormList';
import { CloseOutlined, DeleteTwoTone } from '@ant-design/icons';

import React, { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  addAssetCatalogOptionLabel,
  getAssetCatalogOptionLabelList,
  removeAssetCatalogOptionLabel,
  updateAssetCatalogOptionLabel,
} from 'services/assetconfig';
import { indexBy } from 'utils/utils2';

export type CatalogOptionsLabelModalProps = {
  visible: boolean;
  onCancel: () => void;
  catalogid: number;
  optid: number;
};

const CatalogOptionsLabelModal = ({
  onCancel,
  visible,
  catalogid,
  optid,
}: CatalogOptionsLabelModalProps) => {
  const fieldName = 'labelsList';
  const NEW_CATALOG_OPTION_LABEL_ID = -1;

  const [form] = useForm();

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'labelid',
    addAsFirst: true,
  });


  const { data, refetch, isFetching } = useQuery(
    ['getAssetCatalogOptionLabelList', { catalogid, optid }],
    () =>
      getAssetCatalogOptionLabelList({ catalogid, optid }).then(
        (res) => res.list
      )
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data,
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);

  const addRow = () => {
    const row = {
      labelid: NEW_CATALOG_OPTION_LABEL_ID,
      lancode: '',
      optdescription: '',
      optid: 0,
      optlabel: '',
      opttaglabel: '',
    };
    add(row);
  };

  const onSaveOrEdit = useCallback(
    (record: any) => {
      const relation: any[] = form.getFieldValue(fieldName);
      const index = indexBy(relation, 'labelid')[record.labelid];
      const currentValue = {
        ...relation[index],
      };

      const { labelid } = currentValue;
      labelid === NEW_CATALOG_OPTION_LABEL_ID
        ? addAssetCatalogOptionLabel({
            catalogid: catalogid,
            optid: optid,
            req: { ...currentValue, optid: optid },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          })
        : updateAssetCatalogOptionLabel({
            catalogid: catalogid,
            optid: optid,
            labelid: labelid,
            req: currentValue,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          });
    },
    [form, refetch, catalogid, optid]
  );

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'labelid',
        width: 50,
      },
      {
        dataIndex: 'lancode',
        width: 100,
        render: (value, record) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'labelid')[record.labelid],
              'lancode',
            ]}
          >
            <ParListSelect parcode="LANCODE" />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'optdescription',
        width: 150,
        render: (value, record) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'labelid')[record.labelid],
              'optdescription',
            ]}
          >
            <Input />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'optlabel',
        width: 150,
        render: (value, record) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'labelid')[record.labelid],
              'optlabel',
            ]}
          >
            <Input />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'opttaglabel',
        width: 150,

        render: (value, record) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'labelid')[record.labelid],
              'opttaglabel',
            ]}
          >
            <Input />
          </Form.Item>
        ),
      },

      {
        dataIndex: 'action',
        width: 150,
        render: (value, record) => (
          <Row gutter={8}>
            <Col>
              <UpdateButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(record);
                }}
                comcode={
                  NEW_CATALOG_OPTION_LABEL_ID === record.labelid 
                    ? 'save'
                    : 'update'
                }
              />
            </Col>
            <Col span={8}>
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  NEW_CATALOG_OPTION_LABEL_ID === record.labelid 
                    ? remove(NEW_CATALOG_OPTION_LABEL_ID)
                    : removeAssetCatalogOptionLabel({
                        catalogid: catalogid,
                        labelid: record.labelid,
                        optid: optid,
                      }).then((res) => {
                        notification.success({
                          message: res.message,
                        });
                        refetch();
                      });
                }}
              >
                <DeleteButton
                  onlyIcon
                  danger={NEW_CATALOG_OPTION_LABEL_ID === record.labelid} 
                  icon={
                    NEW_CATALOG_OPTION_LABEL_ID === record.labelid ? ( 
                      <CloseOutlined height="14px" width="14px" color="white" />
                    ) : (
                      <DeleteTwoTone twoToneColor="#eb2f96" />
                    )
                  }
                />
              </Popconfirm>
            </Col>
          </Row>
        ),
      },
    ],
    [form, data]
  );

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      width={1200}
      destroyOnClose
      footer={null}
    >
      <Card>
        <Form form={form}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const labels: any[] = getFieldValue(fieldName);

              const isNewItemInTable =
                labels?.find(
                  ({ labelid }) => labelid === NEW_CATALOG_OPTION_LABEL_ID
                ) !== undefined;

              return (
                <DataGrid
                  rowKey="labelid"
                  titleComcode="catalogoptionslabel"
                  dataSource={labels}
                  columns={columns}
                  hideFilters
                  isLoading={isFetching}
                  extraActions={
                    <ButtonOperation
                      comcode="add"
                      type="primary"
                      htmlType="button"
                      onClick={() => {
                        addRow();
                      }}
                      disabled={isNewItemInTable}
                    />
                  }
                />
              );
            }}
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
};

export default CatalogOptionsLabelModal;
